import React, { useState, useEffect } from "react";
import api from "../../services/api";
import CustomModal from "../Modals/CustomModal";
import KitNaoEntregueForm from "./KitNaoEntregueForm";

import {
  FormContainer,
  FormHeader,
  FormField,
  Message,
  ValidationError,
  ButtonContainer,
  CancelButton,
  NaoEntregueButton,
  ConfirmButton,
} from "./KitEntregaForm.styles";

const KitEntregaForm = ({ gestacao, gestante, onClose, onEntregaRegistrada }) => {
  const [formValues, setFormValues] = useState({
    data_entrega_kits: "",
    quantidade_kits: 0,
    justificativa: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showNaoEntregueModal, setShowNaoEntregueModal] = useState(false);
  const [validacao, setValidacao] = useState(null);
  const [loadingValidacao, setLoadingValidacao] = useState(true);

  useEffect(() => {
    const carregarValidacao = async () => {
      setLoadingValidacao(true);
      try {
        const response = await api.get(`/gestante/gravidez/${gestacao.id}/entrega-kit/validar`);
        setValidacao(response.data);
      } catch (error) {
        console.error("Erro ao validar entrega de kit:", error);
        setValidacao({ elegivel: false, mensagem: "Erro ao verificar elegibilidade para entrega do kit." });
      } finally {
        setLoadingValidacao(false);
      }
    };
    carregarValidacao();
  }, [gestacao.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    if (!formValues.data_entrega_kits) {
      errors.data_entrega_kits = "Data da entrega é obrigatória.";
    }
    const qtdKits = Number(formValues.quantidade_kits);
    if (!formValues.quantidade_kits || qtdKits <= 0) {
      errors.quantidade_kits = "Informe uma quantidade válida de kits.";
    }
    const qtdFilhos = gestacao?.qtd_filhos ?? 0;
    if (qtdKits > qtdFilhos) {
      errors.quantidade_kits =
        "Não é permitido registrar entrega de kits com o número de kits maior que a quantidade de filhos.";
    }
    if (qtdKits > 0 && qtdKits < qtdFilhos) {
      const justificativa = (formValues.justificativa || "").trim();
      if (!justificativa) {
        errors.justificativa =
          "É necessário inserir uma justificativa quando o número de kits entregues for menor que a quantidade de filhos.";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setErrorMessage("Por favor, corrija os erros antes de enviar.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    const payload = {
      data_entrega_kits: formValues.data_entrega_kits,
      quantidade_kits: Number(formValues.quantidade_kits),
      justificativa: (formValues.justificativa || "").trim() || undefined,
    };
    try {
      await api.put(
        `/gestante/${gestante.cpf}/gravidez/entrega-kit`, payload);
      setSuccessMessage("Entrega registrada com sucesso!");
      setFormValues({ data_entrega_kits: "", quantidade_kits: 0, justificativa: "" });
      setValidationErrors({});

      if (onEntregaRegistrada) {
        onEntregaRegistrada(); // ✅ atualiza timeline
      }

      // Fechar modal automaticamente após sucesso, se desejar
      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      const detail =
        error.response?.data?.detail || "Erro ao enviar o formulário.";
      setErrorMessage(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNaoEntregue = () => {
    setShowNaoEntregueModal(true);
  };

  const handleNaoEntregueClose = () => {
    setShowNaoEntregueModal(false);
  };

  const handleNaoEntregueRegistrado = () => {
    if (onEntregaRegistrada) {
      onEntregaRegistrada("A justificativa de kit não entregue registrada com sucesso");
    }
    setShowNaoEntregueModal(false);
    if (onClose) {
      onClose();
    }
  };

  // Exibir loading enquanto a validação é carregada
  if (loadingValidacao) {
    return (
      <FormContainer>
        <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
          Verificando elegibilidade para entrega do kit...
        </p>
      </FormContainer>
    );
  }

  // Se os requisitos não forem atendidos, mostrar mensagem informativa
  if (!validacao?.elegivel) {
    return (
      <>
        <FormContainer>
          <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
            {"Os requisitos para a entrega do Kit ainda não foram atendidos."}
          </p>

          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h4 style={{
              color: '#1a1a1a',
              marginTop: 0,
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Requisitos para a entrega do Kit
            </h4>
            <ul style={{
              marginLeft: '20px',
              marginBottom: 0,
              color: '#2f2f2f',
              fontSize: '13px',
              lineHeight: '1.6'
            }}>
              <li style={{ marginBottom: '8px' }}>
                A gestante precisa ter realizado pelo menos 5 consultas até a 28º semana de gestação. Se a 5ª consulta foi depois da 28ª semana de gestação será necessário fazer pelo menos 7 consulta no total para liberar a entrega do kit. Serão consideradas apenas as consultas que respeitem o intervalo mínimo de 7 dias corridos entre uma e outra e o esquema vacinal deve está completo.
              </li>
            </ul>
          </div>

          <div style={{
            backgroundColor: '#e9ecef',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#595959'
          }}>
            <p style={{ margin: '4px 0' }}>
              <strong>Status atual:</strong>
            </p>
            <p style={{ margin: '4px 0' }}>
              • Consultas totais: <strong>{validacao?.consultas_totais ?? 0} consulta{(validacao?.consultas_totais ?? 0) !== 1 ? 's' : ''}</strong>
            </p>
            <p style={{ margin: '4px 0' }}>
              • Consultas válidas: <strong>{validacao?.consultas_validas ?? 0} consulta{(validacao?.consultas_validas ?? 0) !== 1 ? 's' : ''}</strong>
            </p>
            <p style={{ margin: '4px 0' }}>
              • Consultas inválidas: <strong>{validacao.consultas_totais - validacao.consultas_validas} consulta{(validacao.consultas_totais - validacao.consultas_validas) !== 1 ? 's' : ''}</strong>
            </p>
            {validacao?.requisito_atendido && (
              <p style={{ margin: '4px 0' }}>
                • Requisito Atendido: <strong>{validacao?.elegivel ? 'Sim' : 'Não'}</strong>
              </p>
            )}
            {validacao?.data_limite_28_semanas && (
              <p style={{ margin: '4px 0' }}>
                • Data limite 28 semanas: <strong>{new Date(validacao.data_limite_28_semanas).toLocaleDateString('pt-BR')}</strong>
              </p>
            )}
            {validacao?.data_quinta_consulta && (
              <p style={{ margin: '4px 0' }}>
                • Data da 5ª consulta: <strong>{new Date(validacao.data_quinta_consulta).toLocaleDateString('pt-BR')}</strong>
              </p>
            )}
            {validacao?.vacinacao && (
              <p style={{ margin: '4px 0' }}>
                • Esquema Vacinal: <strong>{validacao.vacinacao}</strong>
              </p>
            )}            
          </div>

          <ButtonContainer>
            <ConfirmButton
              type="button"
              onClick={onClose}
              style={{ backgroundColor: '#595959', width: '100%' }}
            >
              Voltar
            </ConfirmButton>
          </ButtonContainer>
        </FormContainer>

        {/* Modal de Kits Não Entregues */}
        <CustomModal isOpen={showNaoEntregueModal} onClose={handleNaoEntregueClose}>
          <KitNaoEntregueForm
            gestante={gestante}
            gestacao={gestacao}
            onClose={handleNaoEntregueClose}
            onRegistrado={handleNaoEntregueRegistrado}
          />
        </CustomModal>
      </>
    );
  }

  // Se os requisitos forem atendidos, mostrar o formulário normal
  return (
    <>
      <FormContainer>
        <FormHeader>Entrega de Kits</FormHeader>
        <form onSubmit={handleSubmit}>
          <FormField>
            <label htmlFor="data_entrega_kits">Data da Entrega:</label>
            <input
              type="date"
              id="data_entrega_kits"
              name="data_entrega_kits"
              value={formValues.data_entrega_kits}
              onChange={handleChange}
            />
            {validationErrors.data_entrega_kits && (
              <ValidationError>{validationErrors.data_entrega_kits}</ValidationError>
            )}
          </FormField>

          <FormField>
            <label htmlFor="quantidade_kits">Quantidade de Kits Entregues:</label>
            <input
              type="number"
              id="quantidade_kits"
              name="quantidade_kits"
              min={1}
              value={formValues.quantidade_kits}
              onChange={handleChange}
            />
            {validationErrors.quantidade_kits && (
              <ValidationError>{validationErrors.quantidade_kits}</ValidationError>
            )}
          </FormField>

          {(() => {
            const qtdKits = Number(formValues.quantidade_kits);
            const qtdFilhos = gestacao?.qtd_filhos ?? 0;
            const exibirJustificativa = qtdKits > 0 && qtdKits < qtdFilhos;
            return exibirJustificativa ? (
              <FormField>
                <label htmlFor="justificativa">Justificativa</label>
                <p style={{ fontSize: '12px', color: '#666', margin: '-4px 0 8px 0' }}>
                  Obrigatório quando a quantidade de kits for menor que a de filhos (máx. 100 caracteres).
                </p>
                <textarea
                  id="justificativa"
                  name="justificativa"
                  value={formValues.justificativa}
                  onChange={handleChange}
                  maxLength={100}
                  placeholder="Informe o motivo da entrega de menos kits..."
                  rows={3}
                  aria-required="true"
                />
                <span style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '4px' }}>
                  { (formValues.justificativa || '').length }/100
                </span>
                {validationErrors.justificativa && (
                  <ValidationError>{validationErrors.justificativa}</ValidationError>
                )}
              </FormField>
            ) : null;
          })()}

          <ButtonContainer>
            <NaoEntregueButton type="button" onClick={handleNaoEntregue} disabled={isSubmitting}>
              Informar kits não entregues
            </NaoEntregueButton>
            <CancelButton type="button" onClick={onClose} disabled={isSubmitting}>
              Cancelar e voltar
            </CancelButton>
            <ConfirmButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Enviando..." : "Confirmar e salvar"}
            </ConfirmButton>
          </ButtonContainer>
        </form>

        {successMessage && <Message>{successMessage}</Message>}
        {errorMessage && <Message isError>{errorMessage}</Message>}
      </FormContainer>

      {/* Modal de Kits Não Entregues */}
      <CustomModal isOpen={showNaoEntregueModal} onClose={handleNaoEntregueClose}>
        <KitNaoEntregueForm
          gestante={gestante}
          gestacao={gestacao}
          onClose={handleNaoEntregueClose}
          onRegistrado={handleNaoEntregueRegistrado}
        />
      </CustomModal>
    </>
  );
};

export default KitEntregaForm;